import { Copy, Design } from 'blaze-ui/icons'
import { HoverActionOverlay, HoverActionOverlayProps } from 'components/HoverActionOverlay/HoverActionOverlay'
import { DESIGN_PATH } from 'paths'
import { generatePath, useHistory } from 'react-router-dom'
import { MediaObject } from 'types'
import { useModals } from 'almanac-ui/Modal'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'
import { ImageEditor } from 'screens/Calendar/FullScreenPreview/ImageEditor/ImageEditor'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import CopyModal from 'components/CopyModal/CopyModal'
import { useOnItemCopied } from 'components/CopyModal/useOnItemCopied'

export const EditInDesignerOverlay = ({
  onAction,
  onClose,
  mediaObjects,
  integration,
  channel,
  caption,
  ...props
}: Omit<HoverActionOverlayProps, 'onAction' | 'buttonLabel'> & {
  almanacFileKey: string
  onAction?: () => void
  mediaObjects?: MediaObject[]
  integration?: ExternalIntegration
  channel: DesignChannel
  caption?: string
}) => {
  const history = useHistory()
  const { openModal, closeModal } = useModals()
  const { showNextGenContentPreferences: isGenV2 } = useShowNextGenContentPreferences()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  // Read post status from the query cache (file data is already loaded for the preview)
  const cachedQueries = queryClient.getQueriesData<AlmanacFileShowItem>(
    filesKeys.detail(workspaceId, props.almanacFileKey),
  )
  const fileData = cachedQueries?.[0]?.[1]
  const isPosted = fileData?.post_status === PostStatusPropertyValue.Posted

  const { onItemCopied } = useOnItemCopied({
    title: fileData?.title || '',
    fileType: 'design',
  })

  const imageMediaObjects = (mediaObjects ?? []).filter(
    (obj: MediaObject) => obj.mime_type?.startsWith('image/') && obj.source_url && obj.width && obj.height,
  )
  const hasImages = imageMediaObjects.length > 0

  const onEditDesign = () => {
    onClose?.()
    history.push(generatePath(DESIGN_PATH, { key: props.almanacFileKey }))
  }

  const onEditDesignV2 = () => {
    openModal(
      ImageEditor,
      { designKey: props.almanacFileKey, mediaObjects: imageMediaObjects, integration, channel, caption },
      { newStack: true },
    )
  }

  const onCopyDesign = () => {
    openModal(
      CopyModal,
      {
        item: {
          id: props.almanacFileKey,
          title: fileData?.title || '',
          header_emoji: fileData?.emoji || null,
          linked_copies_count: 0,
        },
        itemType: 'design',
        onItemCopied,
        closeModal,
      },
      { newStack: true },
    )
  }

  const getEditAction = () => (isGenV2 && !fileData?.is_generation_v3 ? onEditDesignV2 : onEditDesign)

  if (isGenV2 && !hasImages && !fileData?.is_generation_v3) return <>{props.children}</>

  return (
    <HoverActionOverlay
      {...props}
      onAction={onAction || (isPosted ? onCopyDesign : getEditAction())}
      buttonLabel={isPosted ? 'Copy Design' : 'Edit Design'}
      buttonProps={{
        frontIcon: isPosted ? Copy : Design,
        variant: 'secondary',
        ...props.buttonProps,
      }}
    >
      {props.children}
    </HoverActionOverlay>
  )
}
